import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { isSessionExpiredError } from '../utils/sessionAuth';
import { trackSignup } from '../utils/analytics';
import { 
  onAuthStateChanged, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut 
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { auth, googleProvider, db } from './config';
import { authenticateWithGoogle, authenticateWithEmail, authenticateSignup, logoutFromBackend } from './api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  const [sessionId, setSessionId] = useState(localStorage.getItem('sessionId'));
  /** 同時に複数 API が 401 でも alert / logout は一度だけ */
  const sessionExpiredHandledRef = useRef(false);

  // メール・パスワードでのサインアップ
  const signUp = async (email, password, userInfo = {}) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // ID tokenを取得してバックエンドに送信
      const idToken = await user.getIdToken();
      const backendResponse = await authenticateSignup(idToken, userInfo);
      
      // セッションIDを保存
      if (backendResponse.session_id) {
        setSessionId(backendResponse.session_id);
        localStorage.setItem('sessionId', backendResponse.session_id);
      }
      
      // Firestoreにユーザー情報を保存
      await createUserProfile(user.uid, {
        email: user.email,
        createdAt: new Date().toISOString(),
        ...userInfo
      });
      
      return user;
    } catch (error) {
      console.error('Error signing up:', error);
      throw error;
    }
  };

  // メール・パスワードでのサインイン
  const signIn = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // ID tokenを取得してバックエンドに送信
      const idToken = await user.getIdToken();
      const backendResponse = await authenticateWithEmail(idToken);
      
      // セッションIDを保存
      if (backendResponse.session_id) {
        setSessionId(backendResponse.session_id);
        localStorage.setItem('sessionId', backendResponse.session_id);
      }
      
      return user;
    } catch (error) {
      console.error('Error signing in:', error);
      
      // バックエンド認証でエラーが発生した場合（退会済みユーザーなど）、
      // Firebase認証からもサインアウトする
      if (auth.currentUser) {
        try {
          await signOut(auth);
          setUserProfile(null);
          setSessionId(null);
          localStorage.removeItem('sessionId');
        } catch (signOutError) {
          console.error('Error signing out after backend error:', signOutError);
        }
      }
      
      throw error;
    }
  };

  // Googleでのサインイン（ポップアップまたはリダイレクト）
  const signInWithGoogle = async (useRedirect = false) => {
    try {
      // Google sign-in starting
      
      let result;
      
      if (useRedirect) {
        // リダイレクト方式
        await signInWithRedirect(auth, googleProvider);
        // リダイレクト後の処理は別の関数で処理
        return null;
      } else {
        // ポップアップ方式
        result = await signInWithPopup(auth, googleProvider);
      }
      
      if (!result) return null;
      
      const user = result.user;
      // Google sign-in successful
      
      return await processGoogleUser(user);
    } catch (error) {
      console.error('Error signing in with Google:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      
      // より詳細なエラー情報を提供
      if (error.code === 'auth/popup-blocked') {
        throw new Error('ポップアップがブロックされました。ブラウザの設定を確認してください。');
      } else if (error.code === 'auth/popup-closed-by-user') {
        throw new Error('ログインがキャンセルされました。');
      } else if (error.code === 'auth/unauthorized-domain') {
        throw new Error('認証ドメインが許可されていません。管理者にお問い合わせください。');
      }
      
      throw error;
    }
  };

  // Googleユーザー処理の共通化
  const processGoogleUser = async (user) => {
    try {
      // ID tokenを取得してバックエンドに送信
      const idToken = await user.getIdToken();
      const backendResponse = await authenticateWithGoogle(idToken);
      
      // セッションIDを保存
      if (backendResponse.session_id) {
        setSessionId(backendResponse.session_id);
        localStorage.setItem('sessionId', backendResponse.session_id);
      }
      
      // Firestoreのユーザー情報をチェック
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      let isNewUser = false;
      let needsProfileSetup = false;
      
      if (!userDoc.exists()) {
        // 新規ユーザーの場合、最小限の情報で作成
        isNewUser = true;
        needsProfileSetup = true;
        await createUserProfile(user.uid, {
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL,
          createdAt: new Date().toISOString(),
          provider: 'google',
          profileCompleted: false
        });
        trackSignup('google');
      } else {
        // 既存ユーザーの場合、プロフィール完成状況をチェック
        const userData = userDoc.data();
        needsProfileSetup = !userData.profileCompleted;
      }
      
      return { 
        user, 
        isNewUser, 
        needsProfileSetup 
      };
    } catch (error) {
      console.error('Error processing Google user:', error);
      
      // バックエンド認証でエラーが発生した場合（退会済みユーザーなど）、
      // Firebase認証からもサインアウトする
      if (auth.currentUser) {
        try {
          await signOut(auth);
          setUserProfile(null);
          setSessionId(null);
          localStorage.removeItem('sessionId');
        } catch (signOutError) {
          console.error('Error signing out after backend error:', signOutError);
        }
      }
      
      throw error;
    }
  };

  // リダイレクト結果の処理
  const handleRedirectResult = async () => {
    try {
      const result = await getRedirectResult(auth);
      if (result) {
        // Redirect result processed
        return await processGoogleUser(result.user);
      }
      return null;
    } catch (error) {
      console.error('Error handling redirect result:', error);
      throw error;
    }
  };

  // サインアウト
  const logout = async () => {
    try {
      // バックエンドのセッションも削除
      if (sessionId) {
        try {
          await logoutFromBackend(sessionId);
        } catch (error) {
          console.error('Backend logout error:', error);
        }
      }
      
      await signOut(auth);
      setUserProfile(null);
      setSessionId(null);
      localStorage.removeItem('sessionId');
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  const handleSessionExpired = useCallback(() => {
    if (sessionExpiredHandledRef.current) return;
    sessionExpiredHandledRef.current = true;
    window.alert('セッションが切れました。再度ログインしてください。');
    void logout();
  }, [logout]);

  // Firestoreにユーザープロフィールを作成
  const createUserProfile = async (uid, userInfo) => {
    try {
      await setDoc(doc(db, 'users', uid), userInfo);
      // createUserProfile: setUserProfile
      setUserProfile(userInfo);
    } catch (error) {
      console.error('Error creating user profile:', error);
      throw error;
    }
  };

  // Firestoreからユーザープロフィールを取得
  const getUserProfile = async (uid) => {
    if (!uid) {
      console.warn('getUserProfile called with empty uid');
      setUserProfile(null);
      return null;
    }
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        const profileData = userDoc.data();
        // getUserProfile: setUserProfile
        setUserProfile(profileData);
        return profileData;
      }
      return null;
    } catch (error) {
      console.error('Error getting user profile:', error);
      throw error;
    }
  };

  // Firestoreのユーザープロフィールを更新
  const updateUserProfile = async (uid, userInfo) => {
    try {
      await updateDoc(doc(db, 'users', uid), userInfo);
      setUserProfile(prev => ({ ...prev, ...userInfo }));
      return true;
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  };

  // ID token取得
  const getIdToken = async () => {
    if (currentUser) {
      try {
        const token = await currentUser.getIdToken();
        return token;
      } catch (error) {
        console.error('Error getting ID token:', error);
        throw error;
      }
    }
    return null;
  };

  // メール認証状態をチェック
  const checkEmailVerification = async () => {
    if (currentUser) {
      await currentUser.reload();
      return currentUser.emailVerified;
    }
    return false;
  };

  // メール認証メールを再送信
  const resendEmailVerification = async () => {
    if (currentUser) {
      const { sendEmailVerification } = await import('firebase/auth');
      await sendEmailVerification(currentUser);
    }
  };

  useEffect(() => {
    if (sessionId) {
      sessionExpiredHandledRef.current = false;
    }
  }, [sessionId]);

  // 認証状態の監視
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      
      if (user) {
        // ユーザーがサインインしている場合、プロフィールを取得
        await getUserProfile(user.uid);
      } else {
        // ユーザーがサインアウトした場合、すべての状態をクリア
        setUserProfile(null);
        setSessionId(null);
        localStorage.removeItem('sessionId');
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userProfile,
    loading,
    sessionId,
    handleSessionExpired,
    isSessionExpiredError,
    signUp,
    signIn,
    signInWithGoogle,
    handleRedirectResult,
    logout,
    createUserProfile,
    getUserProfile,
    updateUserProfile,
    getIdToken,
    checkEmailVerification,
    resendEmailVerification
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
